'use client';

/* 地址管理-自提地址列表 */
// React
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
// 三方库/框架
import { useTranslation } from '@/i18n/client';
import to from 'await-to-js';
// 公共方法
import { initAddress } from '@/utils/addressUtils';
import { getJDISiteKeys } from '@/utils/multiSite';
import {
  ADDRESS_CATEGORY_VALUES,
  ADDRESS_EVENT_NAMES,
  addressBusinessLogger,
} from '@/utils/logger/instance/addressBusinessLogger';
// 接口
import { getSavedPickUpList, modifyPickUp } from '@/apis/address/address.api';
import { Address } from '@/apis/address/address.interface';
// 组件
import PickupDetail from '@/components/address/PickupDetail';
import SiteChangeConfirmDialog from '@/components/countrySelect/SiteChangeConfirmDialog';
import UKPageLoading from '@/components/UKPageLoading';
import UKPageNothing, { DefaultImageType } from '@/components/UKPageNothing';
import UKToast from '@/components/UKToast';
import CloseIcon from '@mui/icons-material/Close';
import {
  Checkbox,
  Dialog,
  DialogTitle,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
} from '@mui/material';
// 资源
import DeleteIcon from '@/assets/icons/address/DeleteIcon.svg';
import FrozenIcon from '@/assets/icons/address/FrozenIcon.svg';
import VectorDown from '@/assets/icons/VectorDownBig.svg';
// 枚举
import { PICK_MODIFY_TYPE } from '@/constants/address';
// 样式
import styles from './index.module.scss';

export interface PickupListRef {
  fetchAddressList: () => Promise<void>;
}

const PickupList = forwardRef<PickupListRef, {}>((props, ref) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [addressList, setAddressList] = useState<Address[]>([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPickupDetail, setSelectedPickupDetail] = useState<Address | null>(null);
  const [showSiteChangeDialog, setShowSiteChangeDialog] = useState(false); // 控制跨站点确认弹窗显示
  const [otherSiteAddressInfo, setOtherSiteAddressInfo] = useState<Address | null>(null); // 存储选中的跨站点地址信息
  const [isExpanded, setIsExpanded] = useState(false); // 控制展开/折叠状态

  // 操作锁
  const [savingInProgress, setSavingInProgress] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchAddressList();
  }, []);

  useImperativeHandle(ref, () => ({
    fetchAddressList,
  }));

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // 设置/取消默认地址
  const handleSetDefault = async (address: Address | null, checked: boolean) => {
    if (!address) return;
    const idStr = String(address.pickUpSiteId);
    if (savingInProgress[idStr]) return;
    setSavingInProgress((prev) => ({ ...prev, [idStr]: true })); // 加锁

    const prevList = [...addressList]; // 旧列表
    let newList: Address[] = [];
    if (checked) {
      newList = addressList.map((item) =>
        item.pickUpSiteId === address.pickUpSiteId
          ? { ...item, isDefault: true, defAddress: true }
          : { ...item, isDefault: false, defAddress: false },
      );
    } else {
      newList = addressList.map((item) =>
        item.pickUpSiteId === address.pickUpSiteId
          ? { ...item, isDefault: false, defAddress: false }
          : item,
      );
    }
    setAddressList(newList);

    const modifyType = checked ? PICK_MODIFY_TYPE.DEFAULT : PICK_MODIFY_TYPE.UD_DEFAULT;
    try {
      const res = await modifyPickUp({
        pickUpId: address.pickUpSiteId,
        modifyType,
      });
      if (res?.success) {
        UKToast.show({
          content: checked
            ? `${t('address.defaultPickupSuccess')}`
            : (`${t('address.unDefaultPickupSuccess')}` as any),
          icon: 'success',
        });
      } else {
        throw new Error();
      }
    } catch (err: any) {
      // 操作失败 回退
      setAddressList(prevList);
      UKToast.show({
        content:
          err?.msg ||
          (checked ? `${t('address.defaultPickupFail')}` : `${t('address.unDefaultPickupFail')}`),
        icon: 'error',
      });
    } finally {
      setSavingInProgress((prev) => ({ ...prev, [idStr]: false }));
    }
  };

  // 删除地址
  const handleDeleteAddress = async (address: Address | null) => {
    if (!address) return;
    const idStr = String(address.pickUpSiteId);
    if (savingInProgress[idStr]) return;
    setSavingInProgress((prev) => ({ ...prev, [idStr]: true })); // 加锁

    try {
      const res = await modifyPickUp({
        pickUpId: address?.pickUpSiteId,
        modifyType: PICK_MODIFY_TYPE.UN_SAVE,
      });

      if (res?.success) {
        setAddressList((prevList) =>
          prevList.filter((item) => String(item?.pickUpSiteId) !== idStr),
        );
        UKToast.show({
          content: `${t('address.unSavePickupSuccess')}` as any,
          icon: 'success',
        });
      } else {
        throw new Error();
      }
    } catch (err: any) {
      UKToast.show({
        content: err?.msg || (`${t('address.unSavePickupFail')}` as any),
        icon: 'error',
      });
    } finally {
      setSavingInProgress((prev) => ({ ...prev, [idStr]: false }));
    }
  };

  // 获取地址列表
  const fetchAddressList = async () => {
    setLoading(true);

    // 初始化位置信息
    const [, locationData] = await to<Address | null>(initAddress());

    // 获取地址列表
    const params = {
      countryCode: locationData?.countryCode || getJDISiteKeys()?.['JDI-Country'],
      latitude: locationData?.latitude,
      longitude: locationData?.longitude,
    };
    const [err, result] = await to(getSavedPickUpList(params));

    if (err) {
      setAddressList([]);
      setLoading(false);
      return;
    }

    const { data } = result as any;
    const list = (data?.validPickUps as Address[]) || [];
    const otherSiteList = ((data?.otherCountryPickUps as Address[]) || [])?.map((item) => {
      return {
        ...item,
        otherSite: true, // 跨站点地址
      };
    });
    const unavailableList = ((data?.unValidPickUps as Address[]) || [])?.map((item) => {
      return {
        ...item,
        disableSelection: true, // 不可选
      };
    });

    const addressIdToMatch = locationData?.pickUpSiteId;
    let foundAddress: Address | undefined;

    let combinedList = [...list, ...unavailableList, ...otherSiteList];

    const index = combinedList?.findIndex((addr) => addr.pickUpSiteId === addressIdToMatch);
    if (index !== -1) {
      foundAddress = combinedList[index];
      const filterList = combinedList.filter((addr) => addr.pickUpSiteId !== addressIdToMatch);
      combinedList = [foundAddress, ...filterList];
    }

    setLoading(false);
    // 设置地址列表数据
    setAddressList(combinedList);

    if (result?.code !== '200') {
      // 地址列表查询异常上报
      addressBusinessLogger({
        eventName: ADDRESS_EVENT_NAMES.PICKUP_LIST_QUERY_ERROR,
        category: {
          module: ADDRESS_CATEGORY_VALUES.MODULE.ADDRESS_MANAGE,
          action: ADDRESS_CATEGORY_VALUES.ACTION.REQUEST,
          result: ADDRESS_CATEGORY_VALUES.RESULT.ERROR,
        },
        message: {
          response: JSON.stringify(result),
          error: JSON.stringify(err),
        },
      });
    }
  };

  const handleAddressItemClick = (address: Address) => {
    if (address.otherSite) {
      // 选择跨站点地址 弹窗提示是否要切换站点
      setOtherSiteAddressInfo(address);
      setShowSiteChangeDialog(true);
      return;
    }
  };

  const renderAddressItem = (address: Address, index: number) => {
    const isUnavailable = address.disableSelection;
    const hideCheckbox = address.disableSelection || address.otherSite;

    return (
      <React.Fragment key={index}>
        <div
          className={`${styles.pickupItem} ${isUnavailable ? styles.unavailable : ''}`}
          onClick={() => handleAddressItemClick(address)}
        >
          <div className={styles.pickupBetweenLine}>
            <div className={styles.pickupName}>{address.siteName}</div>
            {address?.isLastUse && address?.lastUseStr ? (
              <div className={styles.lastUsedTag}>{address.lastUseStr}</div>
            ) : null}
          </div>

          <div className={styles.pickupStartLine}>
            <div className={styles.pickupAddress}>{address?.address}</div>
          </div>

          <div className={styles.pickupStartLine}>
            {address.openTimeStr && <div className={styles.pickupTime}>{address.openTimeStr}</div>}
          </div>

          <div className={styles.pickupBetweenLine}>
            <div className={styles.pickFrozen}>
              {address?.frozenFlag && (
                <>
                  <FrozenIcon />
                  <span>{address?.frozenInfo?.text || t('address.frozenSupport')}</span>
                </>
              )}
            </div>

            <div
              className={styles.detailsBtn}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPickupDetail(address);
                setDetailDialogOpen(true);
              }}
            >
              {t('address.lib.details')}
            </div>
          </div>

          <div className={styles.pickupBetweenLine}>
            <div className={styles.pickupStartLine}>
              {hideCheckbox ? (
                <span className={styles.disabledTag}>
                  {address.otherSite ? t('address.pickupUnavailable') : t('address.delivery')}
                </span>
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!address?.isDefault}
                      onChange={(e) => handleSetDefault(address, e?.target?.checked)}
                      color='primary'
                    />
                  }
                  label={
                    <span className={styles.defaultCheckbox}>
                      {address?.defAddress
                        ? `${t('address.defaultAddress')}`
                        : `${t('address.setDefaultAddress')}`}
                    </span>
                  }
                />
              )}
            </div>

            <div className={styles.actionButtons}>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteAddress(address);
                }}
                aria-label={t('address.delete')}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className={styles.pickupListContainer}>
      {loading ? (
        <div className={styles.loadingContainer}>
          <UKPageLoading />
        </div>
      ) : (
        <>
          {addressList && addressList.length > 0 ? (
            <Grid
              className={styles.pickupListContent}
              container
              rowSpacing={1}
              columnSpacing={{ xs: 1, sm: 1, md: 1 }}
            >
              {(!isExpanded ? addressList.slice(0, 10) : addressList).map((address, index) => [
                <Grid size={addressList && addressList?.length === 1 ? 12 : 6} key={index}>
                  {renderAddressItem(address, index)}
                </Grid>,
              ])}
            </Grid>
          ) : (
            <div className={styles.errorPage}>
              <UKPageNothing
                defaultImageType={DefaultImageType.ADDRESS}
                style={{ marginTop: '60px' }}
                text={t('common.nothingMore')}
              />
            </div>
          )}

          {/* 展开更多按钮 */}
          {addressList && addressList.length > 10 && !isExpanded && (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <div
                className={`${styles.expanded} ${styles.personExpand}`}
                onClick={() => handleExpand()}
              >
                {t('address.viewMore')}
                <VectorDown width={10} className={styles.svgExpand} style={{ top: '0px' }} />
              </div>
            </div>
          )}
        </>
      )}

      {/* 自提点详情弹窗 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        aria-labelledby='pickup-detail-dialog-title'
        sx={{
          '& .MuiDialog-paper': {
            width: '800px',
            maxWidth: '800px',
            maxHeight: '600px',
            position: 'relative',
          },
        }}
      >
        <IconButton
          aria-label='close'
          onClick={() => setDetailDialogOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          id='pickup-detail-dialog-title'
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            paddingBottom: 0,
          }}
        >
          {t('address.pickupDetails')}
        </DialogTitle>
        <PickupDetail pickupInfo={selectedPickupDetail} />
      </Dialog>

      {/* 跨站点确认弹窗 */}
      <SiteChangeConfirmDialog
        open={showSiteChangeDialog}
        toCountryCode={otherSiteAddressInfo?.countryCode || ''}
        toCountryName={
          otherSiteAddressInfo?.countryNameMultiLan || otherSiteAddressInfo?.countryName || ''
        }
        setDialogOpen={setShowSiteChangeDialog}
      />
    </div>
  );
});

PickupList.displayName = 'PickupList';

export default PickupList;
